import { EnergySlider } from 'BMapsUI/UIComponents';
import {
    computePrimaryScoreRange,
    permissiveEnd,
    FILTER_SLIDER_STEP,
} from './primary_score_filter';

/**
 * Global primary-score filter slider. Purely presentational — the
 * parent owns the threshold value in its state (per scoring field) and
 * the debounced onChange handler. The slider's bounds, step grid, and
 * readout are derived here from the current `suggestions` and active
 * `primaryScoreField`.
 *
 * The step grid is anchored at the permissive max so dragging back to
 * the leftmost position lands on the permissive value exactly. The
 * restrictive end sits one step past the data minimum so the user can
 * hide every pose by dragging all the way right. The readout shows
 * "(Showing all)" when the slider is within half a step of the
 * permissive end — the same tolerance `buildGlobalScoreFilter` uses to
 * decide the global filter is a no-op.
 *
 * @param {{
 *     suggestions: { frags: object[] }[],
 *     primaryScoreField: 'enSolute'|'bindingScore',
 *     value: number | undefined,
 *     onChange: (threshold: number) => void,
 * }} props
 */
export function GlobalFilterSlider({
    suggestions, primaryScoreField, value, onChange,
}) {
    const range = computePrimaryScoreRange(suggestions, primaryScoreField);
    const permissive = permissiveEnd(range);
    // Default to the permissive end when the parent has no remembered
    // threshold for this field yet — the slider must always render a
    // numeric value to the underlying input.
    const effectiveValue = value ?? permissive;
    // Anchor the step grid at the snapped permissive end. Because
    // `permissive` is already a multiple of `FILTER_SLIDER_STEP`, every
    // step from it (permissive − step, permissive − 2·step, …) is also
    // on grid, so the readout always shows clean 0.5 multiples and the
    // leftmost position lands on `permissive` exactly.
    const stepsToRestrictive = Math.floor(
        (permissive - range.min) / FILTER_SLIDER_STEP
    ) + 1;
    const sliderMin = permissive - stepsToRestrictive * FILTER_SLIDER_STEP;
    // HTML range input emits strings and browsers may introduce tiny
    // float drift around step boundaries, so compare with a tolerance
    // smaller than the step. Mirrors `buildGlobalScoreFilter`'s
    // null-when-permissive check.
    const isShowingAll = effectiveValue >= permissive - FILTER_SLIDER_STEP / 2;
    const fieldLabel = primaryScoreField === 'enSolute'
        ? 'Interaction energy'
        : 'Binding score';
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                padding: '0 1em',
            }}
        >
            <label
                htmlFor="ligandmod-filter-threshold"
                style={{ whiteSpace: 'nowrap', marginRight: '0.5em' }}
            >
                {`Global filter (${fieldLabel}):`}
            </label>
            <EnergySlider
                id="ligandmod-filter-threshold"
                value={effectiveValue}
                onChange={onChange}
                min={sliderMin}
                max={permissive}
                step={FILTER_SLIDER_STEP}
                style={{ flexGrow: 1 }}
            />
            <span style={{ marginLeft: '0.5em', whiteSpace: 'nowrap' }}>
                {isShowingAll ? '(Showing all)' : effectiveValue.toFixed(1)}
            </span>
        </div>
    );
}
